import { logEvent } from 'components/telemetry';
import { editionIdOf, isAudioJournalId, resumePoint, saveResumePoint } from 'helpers/audioJournal';
import { AnalyticsEvent, ListeningTracker } from 'helpers/audioJournalAnalytics';
import { combineEpics, ofType } from 'redux-observable';
import { EMPTY, fromEvent, merge } from 'rxjs';
import { filter, ignoreElements, tap, throttleTime } from 'rxjs/operators';
import { UpdatePlayedMedia, UPDATE_PLAYED_MEDIA } from 'store/data/played/actions';
import {
	UpdateAudioPlaybackState,
	UpdateAudioPlaybackTime,
	UPDATE_AUDIO_PLAYBACK_STATE,
	UPDATE_AUDIO_PLAYBACK_TIME,
} from 'store/temp/actions';
import { ApplicationState, AudioCurrentState } from 'types';

/**
 * Remembers the listening position of an issue (as kept in playedMedia: the latest one) as chapter + offset,
 * so it survives a corrected re-publish.
 */
const resumePointEpic = (action$, state$) =>
	action$.pipe(
		ofType(UPDATE_PLAYED_MEDIA),
		filter((action: UpdatePlayedMedia) => isAudioJournalId(action.articleId)),
		throttleTime(3000, undefined, { leading: true, trailing: true }),
		tap((action: UpdatePlayedMedia) => {
			const state = state$.value as ApplicationState;
			const editionId = editionIdOf(action.articleId);
			const audio = state.dataState.editions.byId[editionId]?.audio;
			const time = state.offlineState.playedMedia[action.articleId]?.time;
			if (audio && time !== undefined) {
				saveResumePoint(editionId, resumePoint(audio, time));
			}
		}),
		ignoreElements()
	);

const send = (events: AnalyticsEvent[]) => events.forEach(([name, params]) => logEvent(name, params));

/**
 * Listening analytics (time spent, chapters listened, reaching the end) from the player's position reports,
 * the same for the web player and the apps' native player.
 */
const listeningEpic = (action$, state$) => {
	let tracker: ListeningTracker | undefined;
	const playback = () => {
		const { audioPlaybackDetails: details, audioPlaybackState: state } = (state$.value as ApplicationState).tempState;
		return { article: details?.articleList?.[details.currentIndex], state, speed: state.playbackSpeed ?? 1 };
	};
	return merge(
		action$.pipe(
			ofType(UPDATE_AUDIO_PLAYBACK_TIME),
			tap((action: UpdateAudioPlaybackTime) => {
				const { article, state, speed } = playback();
				if (tracker && tracker.id !== article?.id) {
					send(tracker.pause(speed));
					tracker = undefined;
				}
				if (article?.audioJournal) {
					tracker = tracker ?? new ListeningTracker(article);
					send(tracker.sample(action.currentTime, Date.now(), state.currentState === AudioCurrentState.Playing, speed));
				}
			})
		),
		action$.pipe(
			ofType(UPDATE_AUDIO_PLAYBACK_STATE),
			filter((action: UpdateAudioPlaybackState) => action.currentState !== AudioCurrentState.Playing && action.currentState !== AudioCurrentState.Buffering),
			tap(() => tracker && send(tracker.pause(playback().speed)))
		),
		// Leaving the app (it may not come back): report the time so far.
		typeof document === 'undefined'
			? EMPTY
			: fromEvent(document, 'visibilitychange').pipe(
					filter(() => document.visibilityState === 'hidden'),
					tap(() => tracker && send(tracker.flush(playback().speed)))
			  )
	).pipe(ignoreElements());
};

export const audioJournalEpic = combineEpics(resumePointEpic, listeningEpic);
